import { type PostHog } from 'posthog-js';

import { authService } from '@rikstv/play-common/src/utils/auth/AuthService';
import { debounce } from '@rikstv/play-common/src/utils/eventHandler/debounce';
import config from '@rikstv/strim/src/config';

import SharedConfig from '../../config';

import {
  experimentEntries,
  expStore,
  mapExperimentStoreState,
  PH_EXP_NAMES,
  posthogLoadedStore,
  waitForPosthogFeatureFlags,
} from './posthogExperiments';

/**
 * HOW TO USE POSTHOG PROVIDER EXPORTS
 *
 * For reporting:
 * Simply import any event or funnel from here and call it (in a useEffect if needed to avoid duplicate events)
 *
 * For experiments:
 *
 * 1. It is useful to use `const { posthogLoaded } = useLoadPosthog()` to ensure that feature flags are loaded before render to avoid flickering
 * 2. Get experiment variant from `const experimentIsOn = usePostHogExperiment('<exp_name>', condition?: boolean)` to get a boolean indicating if the experiment is active (i.e. the user is in the test variation group)
 *
 */

/**
 * Use this hook in situations where you want posthog to init before react renders components that use experiments or events.
 */
export const useLoadPosthog = () => {
  if (!_posthogPromise) {
    lazyLoadClient();
  }
  return posthogLoadedStore();
};

/**
 * Lazy-load posthog client only when events are used to avoid having it part of every bundle.
 * That way it costs us nothing to have it available in the codebase.
 */
let _posthogPromise: Promise<PostHog> | null = null;
const lazyLoadClient = async () => {
  if (_posthogPromise) {
    return _posthogPromise;
  }
  const start = Date.now();
  // Allow max 500ms to wait for posthog loaded state since we might prevent rendering otherwise
  setTimeout(() => posthogLoadedStore.setState({ posthogLoaded: true }), 500);
  _posthogPromise = new Promise(async resolve => {
    const { posthog } = await import('posthog-js');
    posthog.init('phc_kqixdPiJcf3OETOVmnip0rBeBZz6CAZya64q7uN2ZIq', {
      api_host: SharedConfig.application === 'Strim' ? '/ph' : 'https://eu.i.posthog.com',
      ui_host: 'https://eu.posthog.com',
      persistence: 'localStorage',
      // disable all auto capture
      autocapture: false,
      capture_pageview: false,
      capture_performance: false,
      capture_heatmaps: false,
      capture_dead_clicks: false,
      capture_pageleave: false,
      enable_recording_console_log: false,
      before_send: payload => {
        console.log('[posthog] before_send:', payload);
        return config.enablePosthog ? payload : null;
      },
    });
    // Only false in e2e tests where we have mocked feature flag values
    if (waitForPosthogFeatureFlags) {
      posthog.onFeatureFlags(function () {
        posthogLoadedStore.setState({ posthogLoaded: true });
        console.log(`[posthog] initialized in ${Date.now() - start}ms`);
        resolve(posthog);
        expStore.setState(state => {
          // Update store with values based on experiment variation group
          return mapExperimentStoreState((key, name, contender) => {
            // potential override via /dev/null
            if (state[name]) {
              return contender;
            }
            const variant = posthog.getFeatureFlag(key);
            return typeof variant === 'string' ? variant : null;
          });
        });
      });
    } else {
      posthogLoadedStore.setState({ posthogLoaded: true });
      resolve(posthog);
    }
  });
  return _posthogPromise;
};

const reportExperimentImpressionOnce = once(() => {
  const state = expStore.getState();
  // Report experiment impression once per active experiment
  experimentEntries.forEach(([experiment, { key }]) => {
    const variantKey = `${experiment}Variant` as const;
    const group = state[variantKey];
    if (group) {
      window.dataLayer.push({
        event: 'experience_impression',
        experiment_id: key, //unik id for ekseprimentet
        variant_id: group === 'control' ? '0' : '1', //variant, 0=orginal
        experiment_source: 'PH', //LD = Launch Darkly, SAN=Sanity/custom
      });
    }
  });
});

/**
 * Use this hook to get a boolean (true if user is in the contender variant group) for a given experiment. And auto-report impressions to GA.
 * @param experiment - The experiment name
 * @param externalCondition - Optional. When left out or `true` triggers lazy-load of posthog client. When `false` the hook also returns false.
 */
export const usePostHogExperiment = (experiment: PH_EXP_NAMES, externalCondition = true) => {
  if (externalCondition) {
    lazyLoadClient().then(reportExperimentImpressionOnce);
  }

  return expStore()[experiment] && externalCondition;
};

const _captureEvent: PostHog['capture'] = (event_name, properties, options) => {
  console.log('[posthog] captureEvent:', event_name, properties, options);
  lazyLoadClient().then(posthog => {
    const userId = authService.getUserData()?.userId ?? '';
    posthog.capture(event_name, { ...properties, userId }, options);
  });
  return undefined;
};

// Debounce avoids accidental double invocation and rage clicks, because react and hooks...
const captureEvent = debounce(_captureEvent, 150);

export const salesFunnel = {
  captureViewSubscriptions() {
    captureEvent('strim_funnel_1_select');
  },
  captureViewSelectAddons(products: string[]) {
    captureEvent('strim_funnel_select_addons', { products });
  },
  captureGoToSignUp(products: string[]) {
    captureEvent('strim_funnel_2_signup', { products });
  },
  captureRegisteredUser(products: string[], simplifiedSignUpWithVipps: boolean) {
    captureEvent('strim_funnel_3_registered', { products, simplifiedSignUpWithVipps });
  },
  capturePurchaseSuccess(products: string[], paymentAmount: number) {
    captureEvent('strim_funnel_4_purchase', { products, paymentAmount, nrOfAddons: products.length - 1 });
  },
};

export const onboardingFunnel = {
  captureOnboardingImpression() {
    captureEvent('strim_ml_quickstart_impression');
  },
  captureOnboardingOpenDialog() {
    captureEvent('strim_ml_quickstart_open_dialog');
  },
  captureOnboardingCompleted(titleCount: number) {
    captureEvent('strim_ml_quickstart_completed', { titleCount });
  },
};

export const singleSignoutFunnel = {
  captureSignoutButtonClick() {
    captureEvent('strim_single_sign_out_button_click');
  },
  captureInfoIconClick() {
    captureEvent('strim_single_sign_out_info_icon_click');
  },
  captureSignoutCompleted() {
    captureEvent('strim_single_sign_out_completed');
  },
};

export const changePasswordFunnel = {
  captureChangePasswordCompleted(singleSignout: boolean) {
    captureEvent('strim_change_password_completed', { singleSignout });
  },
};

export const voucherIdentificationFunnel = {
  captureVoucherIdentificationOpened(voucherCode: string) {
    captureEvent('strim_funnel_step1_voucher_identification_opened', { voucherCode });
  },
  captureVoucherIdentificationSMSSent(voucherCode: string) {
    captureEvent('strim_funnel_step2_voucher_identification_sms_sent', { voucherCode });
  },
  captureVoucherIdentificationCodeValidated(voucherCode: string) {
    captureEvent('strim_funnel_step3_voucher_identification_code_validated', { voucherCode });
  },
};

export const linearCoverpageFunnel = {
  captureClickLiveNavigation() {
    captureEvent('linear_coverpage_live_navigation');
  },

  captureClickChannelFromChannelGrid(selectedChannelId: number, selectedChannelIndex: number) {
    captureEvent('linear_coverpage_channel_grid_click', {
      selectedChannelId,
      selectedChannelIndex,
    });
  },

  captureClickFromChannelList(selectedChannelId: number, selectedChannelIndex: number, previousChannelIndex: number) {
    captureEvent('linear_coverpage_channel_list_click', {
      selectedChannelId,
      selectedChannelIndex,
      previousChannelIndex,
    });
  },
};

export const stepIndicator = {
  // create separate debounce here since navigation trigger a new event that cancels the last one if we use the general debounced captureEvent
  captureClickStepIndicator: debounce((label: string, state: string) => {
    _captureEvent('strim_step_indicator_click', { label, state });
  }, 150),
};

export const playerRecoveryFunnel = {
  captureAudioDecodeErrorRecoveryAttempt(videoId: string, currentTime: number) {
    captureEvent('audio_decode_error_recovery_attempt', {
      video_id: videoId,
      current_time: currentTime,
    });
  },
};

/**
 * Call with a function to get a new function that will only call the original function once.
 */
function once(fn: () => void) {
  let called = false;
  return () => {
    if (!called) {
      called = true;
      fn();
    }
  };
}
